import { InfoCard } from "../../shared/InfoCard";
import translationKeys from "../../../../contexts/I18nContext/translationKeys";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import {
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  WhmStack,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { useDeleteInvestorMutation } from "../../../../data/useProfile";
import { useContext, useState } from "react";
import { SnackbarContext } from "../../../shared/Snackbar";
import useUserInfo from "../../../../hooks/useUserInfo";
import { ProfileContext } from "../../../../contexts/ProfileContext";
import closeIcon from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../../data/dtos/profile-config.dto";
import TranslationKeys from "../../../../contexts/I18nContext/translationKeys";
import { CustomWhmLoadingButton } from "../../../shared/CustomWhmLoadingButton";

interface SettingsDeleteCardProps {
  permission: ProfilePermissionType;
}

export default function SettingsDeleteCard({
  permission,
}: SettingsDeleteCardProps) {
  const { onLogout } = useContext(ProfileContext);
  const deleteInvestorMutation = useDeleteInvestorMutation();
  const { showSnackbar } = useContext(SnackbarContext);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const userInfo = useUserInfo();

  const isVerificationMissing =
    permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED ||
    permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING;
  const userIsDeleted =
    userInfo.pendingDeletion || deleteInvestorMutation.isSuccess;
  const { pendingDeletion } = userInfo;

  if (pendingDeletion) {
    return (
      <InfoCard
        smallPadding
        title={
          <Translate
            translationKey={translationKeys.DELETE_SECURITY_CARD_TITLE}
          />
        }
        subtitle={
          <Translate
            translationKey={
              translationKeys.DELETE_SECURITY_CARD_SUBTITLE_PENDING_DELETION
            }
          />
        }
      />
    );
  }

  const requestDelete = () =>
    deleteInvestorMutation.mutate(void 0, {
      onSuccess: (response) => {
        setIsConfirmDialogOpen(false);
        showSnackbar({
          severity: "info",
          message: (
            <Translate
              translationKey={translationKeys.DELETE_SECURITY_CARD_SUCCESS}
            />
          ),
        });
        if (response.accountDisabled) {
          onLogout();
          window.location.reload();
        }
      },
      onError: (error) => {
        showSnackbar({
          severity: "error",
          message: <ErrorSnackbar error={error} />,
        });
      },
    });

  return (
    <>
      <InfoCard
        title={
          <Translate
            translationKey={translationKeys.DELETE_SECURITY_CARD_TITLE}
          />
        }
        subtitle={
          <Translate
            translationKey={
              userIsDeleted
                ? translationKeys.DELETE_SECURITY_CARD_CONFIRM_SUBTITLE_PENDING
                : translationKeys.DELETE_SECURITY_CARD_SUBTITLE
            }
          />
        }
        action={
          userIsDeleted ? null : (
            <WhmButton
              variant="outlined"
              color="error"
              onClick={() => setIsConfirmDialogOpen(true)}
              sx={{
                minWidth: "fit-content !important",
                svg: {
                  display: "block !important",
                },
                "&.MuiLoadingButton-root .MuiLoadingButton-loadingIndicator.MuiLoadingButton-loadingIndicatorCenter":
                  {
                    position: "absolute !important",
                  },
              }}
            >
              <Translate
                translationKey={translationKeys.DELETE_SECURITY_CARD_CTA}
              />
            </WhmButton>
          )
        }
        smallPadding
      />
      <WhmDialog open={isConfirmDialogOpen} maxWidth={"xs"}>
        <WhmDialogTitle variant="h4">
          <Translate
            translationKey={translationKeys.DELETE_SECURITY_CARD_CONFIRM_TITLE}
          />
        </WhmDialogTitle>
        <WhmIconButton
          aria-label="close"
          onClick={() => setIsConfirmDialogOpen(false)}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <img src={closeIcon} alt="close" />
        </WhmIconButton>

        <WhmDialogContent>
          <WhmTypography
            variant="body1"
            sx={{ marginBottom: isVerificationMissing ? "8px" : undefined }}
          >
            <TranslateHtml
              translationKey={
                isVerificationMissing
                  ? TranslationKeys.DELETE_SECURITY_CARD_PENDING_VERIFICATION_SUBTITLE
                  : TranslationKeys.DELETE_SECURITY_CARD_CONFIRM_SUBTITLE
              }
            />
          </WhmTypography>
        </WhmDialogContent>
        {!isVerificationMissing && (
          <WhmDialogActions>
            <WhmStack direction="row" justifyContent="flex-end" gap="8px">
              <WhmButton
                variant="outlined"
                color="secondary"
                disabled={deleteInvestorMutation.isPending}
                onClick={() => setIsConfirmDialogOpen(false)}
                sx={{
                  minWidth: "fit-content !important",
                }}
              >
                <Translate
                  translationKey={
                    translationKeys.DELETE_SECURITY_CARD_CONFIRM_CANCEL
                  }
                />
              </WhmButton>
              <CustomWhmLoadingButton
                loading={deleteInvestorMutation.isPending}
                variant="contained"
                color="error"
                onClick={requestDelete}
              >
                <Translate
                  translationKey={
                    deleteInvestorMutation.isPending
                      ? translationKeys.DELETE_SECURITY_CARD_CONFIRM_CTA_PENDING
                      : translationKeys.DELETE_SECURITY_CARD_CONFIRM_CTA
                  }
                />
              </CustomWhmLoadingButton>
            </WhmStack>
          </WhmDialogActions>
        )}
      </WhmDialog>
    </>
  );
}
